import { AnalyticIdentifier } from '@common/neo.tax';
import {
  Button,
  GeneralFormError,
  ModalActions,
  ModalHeader,
  ControlledInput,
  ControlledMonthPicker,
  colors,
} from '@ui-components/neo.tax';
import { useState } from 'react';
import { FreeFocusInside } from 'react-focus-lock';
import { useForm } from 'react-hook-form';

import { useCurrentUser } from '@neo.tax/shared/hooks';
import { useAnalyticId } from '@neo.tax/shared/state';
import { validateIfExist } from '@neo.tax/shared/utils';

import { FormStyled, StyledCompanyIcon } from './create-company.styled';
import { useCreateCompanyForOrganization } from './hooks';
import { CreateCompanyFormValues } from './types';

interface CreateCompanyProps {
  close: () => void;
}

export const CreateCompany = ({ close }: CreateCompanyProps): React.ReactElement => {
  const [createCompanyForOrganization, { loading }] = useCreateCompanyForOrganization();
  const [errorMessage, setErrorMessage] = useState<string>();
  const { user } = useCurrentUser();
  const companyNameInputId = useAnalyticId(AnalyticIdentifier.RTC_CREATE_COMPANY_COMPANY_NAME_INPUT);
  const fiscalYearEndMonthId = useAnalyticId(AnalyticIdentifier.RTC_CREATE_COMPANY_FISCAL_YEAR_END_MONTH);
  const createCompanyId = useAnalyticId(AnalyticIdentifier.RTC_ACCOUNTANT_CREATE_COMPANY);

  const { control, formState, handleSubmit, reset } = useForm<CreateCompanyFormValues>({
    mode: 'onChange',
    defaultValues: {
      companyName: '',
      fiscalYearEndMonth: '11',
    },
  });

  const onSubmit = async (values: CreateCompanyFormValues): Promise<void> => {
    if (!user || !user.organization) {
      throw new Error('Tried creating a new company without a current user or organization');
    }
    try {
      await createCompanyForOrganization({
        variables: {
          organizationId: user.organization.id,
          createCompanyForOrganizationInput: {
            companyName: values.companyName,
            fiscalYearEndMonth: Number.parseInt(values.fiscalYearEndMonth, 10),
          },
        },
      });
      close();
      reset();
    } catch {
      setErrorMessage('Something went wrong. Please try again.');
    }
  };

  const onClickCancel = (): void => {
    close();
    reset();
  };

  return (
    <FreeFocusInside>
      <FormStyled onSubmit={handleSubmit(onSubmit)}>
        <ModalHeader
          copy="Create a company to start working on their R&amp;D credit."
          icon={<StyledCompanyIcon />}
          iconBgColor={colors.blue[50]}
          title="Create New Company"
        />
        <ControlledInput
          control={control}
          name="companyName"
          rules={{ required: true, validate: validateIfExist }}
          id={companyNameInputId}
          label="Company Name*"
          placeholder="Company Name"
          horizontal
        />
        <ControlledMonthPicker
          control={control}
          id={fiscalYearEndMonthId}
          name="fiscalYearEndMonth"
          label="What month does your fiscal year end?*"
          helpText="Most companies' fiscal year ends in December."
          rules={{
            required: 'Please select a month',
          }}
          horizontal
          usePortal
        />
        <ModalActions>
          <Button disabled={loading} onClick={onClickCancel} rounded="regular" size="base" variant="white">
            Cancel
          </Button>
          <Button
            disabled={!formState.isValid || loading}
            rounded="regular"
            size="base"
            type="submit"
            variant="primary"
            id={createCompanyId}
          >
            {loading ? 'Creating Company...' : 'Create New Company'}
          </Button>
        </ModalActions>
        <GeneralFormError centered error={!!errorMessage}>
          {errorMessage}
        </GeneralFormError>
      </FormStyled>
    </FreeFocusInside>
  );
};
